// @flow
import invariant from 'invariant';
import queryString from 'query-string';
import trackError from 'utils/trackError';
import { filterNulls, isNotEmpty } from 'utils/functional';
import { isBrowser } from 'utils/browser';
import { createCookie, getCookieValue, deleteCookie } from 'utils/cookies';
import * as mixpanelUtils from 'utils/mixpanel';
import { configValue } from 'utils/config';
import { getItem, setItem } from 'utils/localStorage';
import { log } from 'utils/logging';
import type { CurrentUser } from 'api/currentUser';
import type { CurrentUser as GradFairsAppCurrentUser } from 'api/gradFairs';

const validateEvent = (eventName: string, properties: Object) => {
  if (configValue('idealist', 'analytics', 'enforceValidEvents')) {
    invariant(
      !eventName.includes('_') && /^[A-Z0-9]/.test(eventName),
      `Invalid Analytics event name: "${eventName}"`
    );

    Object.keys(properties).forEach(name => {
      invariant(
        /^[a-z0-9_]+$/.test(name),
        `Invalid Analytics property name: "${name}". Property names must be snake case`
      );
    });
  }
};

let registeredAbExperiments: ?string = null;

export const registerAbExperiments = (
  abExperiments: ?Array<{ id: string, created: string, bucket: string }>
) => {
  registeredAbExperiments =
    abExperiments && isNotEmpty(abExperiments)
      ? `"${abExperiments.map(e => `${e.id}=${e.bucket}`).join('"&"')}"`
      : null;
  if (registeredAbExperiments !== null) {
    mixpanelUtils.register({
      experiments: registeredAbExperiments
    });
  } else {
    mixpanelUtils.unregister('experiments');
  }
};

const windowDataLayerPush = (eventData: Object) => {
  if (registeredAbExperiments !== null) {
    eventData = {
      ...eventData,
      experiments: registeredAbExperiments
    };
  }
  if (getCookieValue('_traceAnalytics')) {
    const trace = getItem('_analyticsTrace') || [];
    trace.push(eventData);
    setItem('_analyticsTrace', trace);
  }
  window.dataLayer.push(eventData);
};

export const trackEvent = (eventName: string, ...args: any) => {
  let properties = {};
  let callback = null;

  if (args.length === 2) {
    properties = args[0];
    callback = args[1];
  } else if (args.length === 1) {
    if (typeof args[0] === 'function') {
      callback = args[0];
    } else {
      properties = args[0];
    }
  }

  validateEvent(eventName, properties);
  windowDataLayerPush({
    event: eventName,
    ...properties
  });
  mixpanelUtils.track(eventName, properties, callback);
  log('Triggering analytics event', {
    event: eventName,
    ...properties
  });
};

const EVENTS = {};

export const trackEventOnce = (eventName: string, ...args: any) => {
  if (!isBrowser()) return;

  const key = `${eventName}${JSON.stringify(args)}${window.location.pathname}${
    window.location.search
  }`;
  if (!EVENTS[key]) {
    EVENTS[key] = true;
    trackEvent(eventName, ...args);
  }
};

export const trackPageView = (pageName: string, properties: Object = {}) => {
  // Mixpanel assumes `id` refers to `userId`, so let's
  // rename to `listing_id` to keep things easier to parse.
  if (properties && properties.id) {
    properties.listing_id = properties.id;
    delete properties.id;
  }

  trackEvent(`Page View: ${pageName}`, properties);
};

export const trackModalView = (modalName: string, properties: Object = {}) => {
  trackEvent(`Modal View: ${modalName}`, properties);
};

export const trackClicked = (name: string, ...args: any) => {
  trackEvent(`Clicked: ${name}`, ...args);
};

// only use for external links, not with react-router
export const setupLinkTracker = (name: string, properties?: Object = {}) => (
  el: ?Element
) => {
  if (el) {
    if (!el.hasAttribute('href') || el.getAttribute('href') === '#') {
      throw new Error('Attempted to track link with no url');
    }
    properties.url = el.getAttribute('href');
    const eventName = `Clicked: ${name}`;
    validateEvent(eventName, properties);
    mixpanelUtils.trackLinks(el, eventName, properties);
    const clickHandler = () => {
      windowDataLayerPush({
        event: eventName,
        ...properties
      });
      log('Triggering analytics event', {
        event: eventName,
        ...properties
      });
    };
    el.addEventListener('click', clickHandler);
    return clickHandler;
  }
};

const getUserType = (user: CurrentUser | GradFairsAppCurrentUser) => {
  if (user.id) {
    if (user.permissions && user.permissions.includes('staff')) {
      return 'Staff';
    }
    if (user.orgs && user.orgs.length > 0) {
      let orgUserType = 'Org Admin';
      user.orgs.forEach(org => {
        if (org.hasGradProgram) {
          orgUserType = 'Grad Admin';
        }
      });
      return orgUserType;
    }
    if (user.groups && user.groups.length > 0) {
      return 'Group Admin';
    }
    return 'Logged In User';
  }
  return 'Logged Out User';
};

export const trackLogin = (
  user: CurrentUser | GradFairsAppCurrentUser,
  method: string
) => {
  trackEvent('User Logged In', {
    user_id: user.id,
    method,
    user_type: getUserType(user)
  });
  if (user.id) {
    mixpanelUtils.register({
      user_id: user.id,
      user_type: getUserType(user)
    });
  } else {
    trackError('Missing user id after log in');
  }
  deleteCookie('justLoggedIn');
};

export const trackSignUp = (
  user: CurrentUser | GradFairsAppCurrentUser,
  method: string
) => {
  trackEvent('User Signed Up', {
    user_id: user.id,
    method,
    user_type: getUserType(user)
  });
  if (user.id) {
    mixpanelUtils.register({
      user_id: user.id,
      user_type: getUserType(user)
    });
  } else {
    trackError('Missing user id after sign up');
  }
  deleteCookie('justSignedUp');
};

export const trackLogout = () => {
  mixpanelUtils.resetUser();
  trackEvent('User Logged Out');
  deleteCookie('justLoggedOut');
};

export const bootstrapAnalytics = ({
  user
}: {
  user: CurrentUser | GradFairsAppCurrentUser
}) => {
  registerAbExperiments(user.abExperiments);
  if (getCookieValue('justSignedUp')) {
    const method = getCookieValue('justSignedUp');
    trackSignUp(user, method);
  }
  if (getCookieValue('justLoggedIn')) {
    const method = getCookieValue('justLoggedIn');
    trackLogin(user, method);
  }
  if (getCookieValue('justLoggedOut')) {
    trackLogout();
  }
  if (!user.id) {
    mixpanelUtils.unregister('user_id');
    mixpanelUtils.register({
      user_type: getUserType(user)
    });
  }

  const params = queryString.parse(window.location.search);
  const analyticsData = filterNulls({
    locale: USER_LOCALE,
    user_id: user.id,
    email_id: params.email_id,
    org_count: user.id ? user.orgs.length : null,
    user_type: getUserType(user)
  });
  const extraParams = filterNulls({
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term,
    sf_campaign_id: params.sf_campaign_id
  });
  // These stored "extra" params are currently only used with the
  // donate form, so we want to avoid adding them elsewhere
  // like window.dataLayer.
  let storedExtraParams = {};
  if (getCookieValue('extraParams')) {
    storedExtraParams = JSON.parse(getCookieValue('extraParams'));
  }
  const extraParamsToStore = filterNulls({
    utm_source: params.utm_source || storedExtraParams.utm_source,
    utm_medium: params.utm_medium || storedExtraParams.utm_medium,
    utm_campaign: params.utm_campaign || storedExtraParams.utm_campaign,
    utm_content: params.utm_content || storedExtraParams.utm_content,
    utm_term: params.utm_term || storedExtraParams.utm_term,
    sf_campaign_id: params.sf_campaign_id || storedExtraParams.sf_campaign_id
  });
  createCookie('extraParams', JSON.stringify(extraParamsToStore), 7);

  windowDataLayerPush({
    event: 'Full Page Load',
    ...analyticsData,
    ...extraParams,
    logged_in_status: user.id ? 'Logged In' : 'Not Logged In'
  });
  mixpanelUtils.registerOnce(analyticsData);
};
